import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Checkbox, FormControl, InputLabel, ListItemText, makeStyles, MenuItem, OutlinedInput, Select, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { selectMenuProps } from "../../../../util";
import LoadingSpinner from "../../LoadingSpinner";

const useStyles = makeStyles({
  select: {
    overflow: "hidden",
  },
  renderValueBox: {
    minWidth: 0,
    overflow: "hidden",
  },
  inputLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "calc(100% - 36px)",
    "&.MuiInputLabel-shrink": {
      overflow: "visible",
    },
  },
  item: {
    whiteSpace: "nowrap",
    "&:not(:last-child)::after": {
      content: '","',
      marginRight: 4,
    },
  },
});

const MultiSelectDropdown = ({
  title,
  values,
  onChange,
  defaultSelected = [],
  loadingMessage,
  loading = false,
  error = false,
  emptyMessage,
  enableIntlOption = true
}) => {
  const classes = useStyles() 
  const [selectedItems, setSelectedItems] = useState(defaultSelected);

  const handleChange = (newValues) => {
    setSelectedItems(newValues);
    onChange(newValues);
  };

  return (
    <FormControl fullWidth variant="outlined" size="small">
      {title && (
        <InputLabel
          id="multi-select-dropdown-label"
          className={classes.inputLabel}
        >
          <FormattedMessage id={title} />
        </InputLabel>
      )}
      <Select
        labelId="multi-select-dropdown-label"
        id="multi-select-dropdown"
        multiple
        value={selectedItems}
        MenuProps={selectMenuProps}
        onChange={(e) => handleChange(e.target.value)}
        input={<OutlinedInput label={<FormattedMessage id={title} />} />}
        inputProps={{ className: classes.select }}
        renderValue={(selected) => {
          const selectedValues = new Set(selected);

          if (selected.length === values.length) {
            return <FormattedMessage id="app.all" />;
          }

          return (
            <Box
              display="flex"
              className={classes.renderValueBox}
            >
              {values
                .filter(({ value }) => selectedValues.has(value))
                .map(({ value, label, intlValues }, index, array) => {
                  const content =
                    enableIntlOption && label.startsWith("app.") ? (
                      <FormattedMessage
                        id={label}
                        values={intlValues ?? {}}
                      />
                    ) : (
                      label
                    );

                  return (
                    <span
                      key={value}
                      className={classes.item}
                    >
                      {content}
                    </span>
                  );
                })}
            </Box>
          );
        }}
      >
        {loading && (
          <MenuItem disabled>
            <LoadingSpinner
              message={loadingMessage ? <FormattedMessage id={loadingMessage} /> : null}
              size={24}
            />
          </MenuItem>
        )}

        {error && (
          <MenuItem disabled>
            <FormattedMessage
              id={error}
              defaultMessage={error}
            />
          </MenuItem>
        )}

        {!loading && !error && values.length === 0 && emptyMessage && (
          <MenuItem disabled>
            <Typography component="i">
              <FormattedMessage id={emptyMessage} />
            </Typography>
          </MenuItem>
        )}

        {values.map(({ value, label, intlValues }) => (
          <MenuItem
            key={value}
            value={value}
          >
            <Checkbox
              checked={selectedItems.includes(value)}
              color="primary"
            />
            <ListItemText
              primary={
                enableIntlOption ? (
                  <FormattedMessage
                    id={label}
                    values={intlValues ?? {}}
                  />
                ) : (
                  <>{label}</>
                )
              }
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
});

MultiSelectDropdown.propTypes = {
  title: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(optionShape).isRequired,
  onChange: PropTypes.func.isRequired,
  defaultSelected: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
};

export default React.memo(MultiSelectDropdown);
